import { useTranslation } from 'react-i18next';

interface Props {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
  // Optional: when provided, the component renders a compact summary line below
  // the preset buttons showing the selected period length + range.
  showSummary?: boolean;
  // Optional: the configured current test/survey period. When provided, a
  // one-tap "Survey period" preset is shown that selects this inclusive range.
  surveyPeriod?: { from: string; to: string } | null;
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function firstOfMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function daysBetween(from: string, to: string): number {
  if (!from || !to) return 0;
  const ms = new Date(to).getTime() - new Date(from).getTime();
  return Math.max(1, Math.round(ms / (24 * 3600 * 1000)) + 1);
}

/**
 * Shared date-range filter used by both ResearchDashboard and GamblingAnalysis.
 * Exposes one-tap presets (Today / 7d / 30d / This month / All time) plus
 * manual date pickers. Shows a short period summary so researchers always
 * know what period they are currently viewing.
 */
export default function DateRangePresets({ dateFrom, dateTo, onChange, showSummary = true, surveyPeriod = null }: Props) {
  const { t } = useTranslation();
  const today = todayIso();
  const isSurvey = !!surveyPeriod && dateFrom === surveyPeriod.from && dateTo === surveyPeriod.to;

  // Detect which preset the current state corresponds to, for highlighting.
  const isAllTime    = !dateFrom && !dateTo;
  const isToday      = dateFrom === today && dateTo === today;
  const isLast7      = !isAllTime && dateFrom === isoDaysAgo(6) && dateTo === today;
  const isLast30     = !isAllTime && dateFrom === isoDaysAgo(29) && dateTo === today;
  const isThisMonth  = !isAllTime && dateFrom === firstOfMonthIso() && dateTo === today;
  const activePreset = isAllTime ? 'all' : isToday ? 'today' : isLast7 ? '7d' : isLast30 ? '30d' : isThisMonth ? 'month' : 'custom';

  const baseBtn = 'px-2.5 py-1 rounded text-xs font-semibold transition-colors';
  const activeBtn = 'bg-amber-600 text-white';
  const idleBtn = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600';

  const btn = (id: string, label: string, from: string, to: string) => (
    <button
      type="button"
      onClick={() => onChange(from, to)}
      className={`${baseBtn} ${activePreset === id ? activeBtn : idleBtn}`}
    >
      {label}
    </button>
  );

  return (
    <div className="inline-flex items-center gap-1 flex-wrap">
      {surveyPeriod && (
        <button
          type="button"
          onClick={() => onChange(surveyPeriod.from, surveyPeriod.to)}
          className={`${baseBtn} ${isSurvey ? 'bg-fuchsia-600 text-white' : 'bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-200 dark:hover:bg-fuchsia-900/60'}`}
        >
          {t('dateRange.surveyPeriod', '調査期間')}
        </button>
      )}
      {btn('today', t('dateRange.today'),       today,               today)}
      {btn('7d',    t('dateRange.last7days'),   isoDaysAgo(6),       today)}
      {btn('30d',   t('dateRange.last30days'),  isoDaysAgo(29),      today)}
      {btn('month', t('dateRange.thisMonth'),   firstOfMonthIso(),   today)}
      {btn('all',   t('dateRange.allTime'),     '',                  '')}
      <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
      <input
        type="date"
        value={dateFrom}
        max={dateTo || undefined}
        onChange={e => onChange(e.target.value, dateTo)}
        className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-xs"
      />
      <span className="text-gray-400">–</span>
      <input
        type="date"
        value={dateTo}
        min={dateFrom || undefined}
        max={today}
        onChange={e => onChange(dateFrom, e.target.value)}
        className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-xs"
      />
      {showSummary && (
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {isAllTime
            ? `· ${t('dateRange.allTime')}`
            : `· ${t('dateRange.periodDays', { days: daysBetween(dateFrom, dateTo) })}`}
        </span>
      )}
    </div>
  );
}
